import { ChangeEvent, forwardRef, useEffect, KeyboardEvent } from 'react'

import { TextField, TextFieldProps } from '@mui/material'
import { useIMask, ReactMaskOpts } from 'react-imask'
import { removeNonNumeric } from '@/helpers'

type MaskedInputProps = Omit<TextFieldProps, 'ref'> & {
  mask?: string | ReactMaskOpts
  format?: 'currency' | 'percentage'
}

export const MaskedInput = forwardRef<HTMLDivElement, MaskedInputProps>(
  ({ mask, value, format, ...props }, ref) => {
    const maskOpts = typeof mask === 'string' ? { mask } : mask || {}

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      const clonedEvent = { ...event }

      // int value is a number like 1010
      let intValue = getIntValue(value)

      if (format === 'currency') {
        // set input value with decimal parts
        clonedEvent.target.value = (intValue / 100).toString()
      }

      if (format === 'percentage') {
        // ensure max value is 100
        if (intValue / 100 > 100) {
          // remove last number
          intValue = Math.floor(intValue / 10)
        }
        // set input value with decimal parts
        clonedEvent.target.value = (intValue / 100).toString()
      }

      props.onChange?.(clonedEvent)
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (format !== 'percentage') return

      if (!['Backspace', 'Delete'].includes(event.key)) return

      const cleanValue = removeNonNumeric(value as string)
      // ignore the % sign and remove last number
      event.currentTarget.value = cleanValue.slice(0, -1)
      handleChange(event as unknown as ChangeEvent<HTMLInputElement>)
    }

    const {
      ref: maskRef,
      value: maskValue,
      setValue: setMaskValue,
    } = useIMask(maskOpts, {
      onAccept: (value) => {
        props.onChange?.({
          target: { value },
        } as React.ChangeEvent<HTMLInputElement>)
      },
    })

    useEffect(() => {
      if (
        value !== undefined &&
        value !== maskValue &&
        typeof value === 'string'
      ) {
        switch (format) {
          case 'currency':
            setMaskValue(currencyMask(value))
            break
          case 'percentage':
            setMaskValue(percentageMask(value))
            break
          default:
            setMaskValue(value)
        }
      }
    }, [value, maskValue, setMaskValue, format])

    return (
      <TextField
        {...props}
        inputRef={maskRef}
        ref={ref}
        value={maskValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
    )
  },
)

const percentageMask = (value: string) => {
  if (value === '') return value

  const percentValue = parseFloat(value) / 100

  return percentValue.toLocaleString('pt-BR', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const currencyMask = (value: string) => {
  if (value === '') return value

  const floatValue = parseFloat(value)

  return floatValue.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const getIntValue = (value: string) => {
  const cleanValue = removeNonNumeric(value)
  return cleanValue ? parseInt(cleanValue, 10) : 0
}
